import { useState } from "react";
import { useTranslation } from "next-i18next";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import ButtonHelp from "@atoms/ButtonHelp";
import { LinkMore } from "@atoms/LinkMore";
import { ProductCard } from "@atoms/ProductCard";
import { ProductCardCarouselData } from "@atoms/ProductCard/ProductCardCarousel";
import { Section } from "@atoms/Section";
import { WideCard } from "@atoms/WideCard";
import color from "@definitions/mui/color";
import Text from "@ds/components/Text";
import { formattedDate } from "@helpers/dateTime";
import useResponsive from "@helpers/useResponsive";
import Footer from "@layouts/Footer";
import { useGetInsightQuery } from "@repositories/insight/insight.redux";
import useStyles from "../styles";
import dynamic from "next/dynamic";

const ProductCardCarousel = dynamic(() => import("@atoms/ProductCard/ProductCardCarousel").then(mod => mod.ProductCardCarousel));

const InsightLiteContainer: React.FC<any> = ({ dataNavbar, insightList }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: dataInsight } = useGetInsightQuery({
    page: currentPage,
  });
  const isMobile = useResponsive("md");
  const classes = useStyles({ isVisible: true });
  const { t } = useTranslation("common");

  const handleNextPage = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const loadMore = () => {
    if (!dataInsight?.meta?.totalPages) {
      return null;
    }

    const { page, totalPages } = dataInsight?.meta;

    if (page < totalPages) {
      return (
        <Box textAlign="center" mt={6}>
          <LinkMore text={t("insight.more")} onClick={handleNextPage} />
        </Box>
      );
    }
  };

  const renderSections = () => {
    if (!insightList?.data) {
      return;
    }

    const firstSectionData = insightList?.data.slice(0, 4);
    const restData = insightList?.data.slice(4);

    const windowInnerHeight = typeof window === "undefined"
      ? 0
      : window.innerHeight;

    const firstSection = (
      <Section maxWidth="full-width" pb={0}>
        <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} width={isMobile ? "100%" : undefined}>
              {firstSectionData?.[0] && (
                <ProductCard
                  title={firstSectionData[0].title}
                  htmlDescription={firstSectionData[0].story}
                  textColor={color.general.main}
                  subTitle={formattedDate(firstSectionData[0].date)}
                  image={firstSectionData[0].imageUrl.mediaPath + "?tr=w-640"}
                  label={firstSectionData[0].category}
                  imageHeight={isMobile ? 200 - (windowInnerHeight < 800 ? 10 : 0) : 300}
                  imgQuality={50}
                  height={isMobile ? "auto" : 474}
                  overflow="hidden"
                  linkTo={`/insight/${firstSectionData[0].slug}`}
                  withImagePlaceholder
                  priority
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {firstSectionData?.slice(1).map((item: any) => (
                <WideCard
                  key={item.id}
                  date={formattedDate(item.date)}
                  description={item.story}
                  title={item.title}
                  image={item.imageUrl.mediaPath + "?tr=w-400"}
                  linkTo={`/insight/${item.slug}`}
                  subtitle={item.category}
                  type="border"
                />
              ))}
            </Grid>
          </Grid>
        </Container>
      </Section>
    );

    const parsedCategoriesDataForCarousel = (): ProductCardCarouselData[] =>
      restData.map((item: any) => ({
        id: item.id,
        image: item.imageUrl.mediaPath,
        linkTo: `/insight/${item.slug}`,
        subTitle: formattedDate(item.date),
        title: item.title,
        label: item.category,
      })) || [];

    return (
      <>
        {firstSection}
        <Section maxWidth="full-width" pt={2} pb={3}>
          <Container maxWidth="lg" disableGutters>
            {isMobile ? (
              <ProductCardCarousel data={parsedCategoriesDataForCarousel()} />
            ) : (
              <Grid container spacing={3}>
                {restData.map((item: any) => (
                  <Grid item xs={6} mb={2} key={item.id} width={isMobile ? "100%" : undefined}>
                    <ProductCard
                      title={item.title}
                      htmlDescription={item.story}
                      subTitle={formattedDate(item.date)}
                      image={item.imageUrl.mediaPath + "?tr=w-500"}
                      imageHeight={200}
                      imgQuality={50}
                      height="376px"
                      overflow="hidden"
                      withImagePlaceholder
                      titleMaxLine={2}
                      linkTo={`/insight/${item.slug}`}
                      label={item.category}
                      textColor={color.general.main}
                    />
                  </Grid>
                ))}
                {loadMore()}
              </Grid>
            )}
          </Container>
        </Section>
      </>
    );
  };

  return (
    <>
      <Box
        className={classes.base}
        id="insight"
        sx={{
          img: {
            maxHeight: "200px",
          },
        }}
      >
        <Box mb={1} mt={5} pb={8}>
          <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
            <Box mb={1}>
              <Text
                align={isMobile ? "center" : "left"}
                variant="h1"
                weight="700"
                sx={{ fontSize: 32 }}
              >
                {t("insight.title")}
              </Text>
            </Box>
            <Text
              variant="body1"
              color="inactive-secondary"
              align={isMobile ? "center" : "left"}
              component="p"
            >
              {t("insight.desc")}
            </Text>
          </Container>
          {renderSections()}
        </Box>
        <ButtonHelp />
        <Footer dataNavbar={dataNavbar} />
      </Box>
    </>
  );
};

export default InsightLiteContainer;
